import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Grid,
  Chip,
  Avatar,
  Divider,
  Stack,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  ImageList,
  ImageListItem,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import EventIcon from "@mui/icons-material/Event";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";

// ------- helpers -------
import { getStatusColor, getStatusIcon } from "../utils/statusHelpers";
import { formatDate, formatDateShort } from "../utils/formatHelpers"; // Keep this import
import { generateJobSheetPDF } from "../utils/jobSheetPDFGenerator"; // Updated path

function JobViewer({ jobId, onBack }) {
  const [job, setJob] = useState(null);
  const [template, setTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = Details, 1 = Photos
  const [photoDialog, setPhotoDialog] = useState({ open: false, photos: [] });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load job data
  useEffect(() => {
    if (!jobId) {
      setError("No Job ID provided.");
      setIsLoading(false);
      return;
    }

    const ref = doc(db, "jobs", jobId);
    const unsub = onSnapshot(
      ref,
      async (snap) => {
        if (snap.exists()) {
          const jobData = { id: snap.id, ...snap.data() };
          setJob(jobData);
          
          // Load template if available
          if (jobData.jobTemplateId) {
            try {
              const templateRef = doc(db, "jobSheetTemplates", jobData.jobTemplateId);
              const templateSnap = await getDoc(templateRef);
              if (templateSnap.exists()) {
                setTemplate(templateSnap.data());
              }
            } catch (err) {
              console.error("Error loading template:", err);
            }
          }
        } else {
          setError("Job not found.");
        }
        setIsLoading(false);
      },
      (err) => {
        console.error(err);
        setError("Failed to load job data.");
        setIsLoading(false);
      }
    );

    return () => unsub();
  }, [jobId]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Extract fields from job - supports both new completionData and legacy formData formats
  const { textFields, imageFields, stats } = useMemo(() => {
    if (!job) return { textFields: [], imageFields: [], stats: {} };

    let fields = [];

    // Check for new completionData format (flat object with field values)
    if (job.completionData && typeof job.completionData === 'object' && !Array.isArray(job.completionData)) {
      // Use template fields if available
      if (template?.fields) {
        fields = template.fields.map(field => ({
          id: field.id,
          label: field.label,
          type: field.type,
          value: job.completionData[field.id] ?? null,
        }));
      } else {
        // Fallback: create fields from completionData keys
        fields = Object.entries(job.completionData).map(([key, value]) => ({
          id: key,
          label: key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim(),
          type: Array.isArray(value) ? 'photo' : typeof value === 'boolean' ? 'checkbox' : 'text',
          value: value,
        }));
      }
    }
    // Legacy formData format (array of submissions)
    else if (job.formData && Array.isArray(job.formData)) {
      const submissions = job.formData;
      const submission = submissions[submissions.length - 1];
      fields = submission?.fields || [];
    }

    // Separate text fields and image fields
    const textFields = fields.filter(
      (f) => f.type !== "photo" && f.type !== "signature"
    );
    
    // Handle photo fields - they may contain arrays of URLs
    const imageFields = [];
    fields.forEach(f => {
      if (f.type === "photo" && f.value) {
        if (Array.isArray(f.value)) {
          f.value.forEach((url, idx) => {
            imageFields.push({
              ...f,
              label: `${f.label} (${idx + 1})`,
              value: url,
            });
          });
        } else {
          imageFields.push(f);
        }
      } else if (f.type === "signature" && f.value) {
        imageFields.push(f);
      }
    });

    // Statistics
    const totalQuestions = textFields.filter((f) => f.type !== "section").length;
    const answeredQuestions = textFields.filter(
      (f) => f.type !== "section" && f.value != null && f.value !== ""
    ).length;
    const totalPhotos = imageFields.length;
    const hasSignature = !!job.completionData?.signatureUrl || imageFields.some(f => f.type === 'signature');

    return {
      textFields,
      imageFields,
      stats: {
        totalQuestions,
        answeredQuestions,
        totalPhotos,
        hasSignature,
        completionPercentage:
          totalQuestions > 0
            ? Math.round((answeredQuestions / totalQuestions) * 100)
            : 0,
      },
    };
  }, [job, template]);

  // ------- PDF generation -------
  const handleDownloadPDF = async () => {
    if (!job) return;
    setIsGenerating(true);

    try {
      const result = await generateJobSheetPDF(job);
      if (result.success) {
        showSnackbar("PDF downloaded successfully!", "success");
      } else {
        showSnackbar("Could not generate PDF: " + result.error, "error");
      }
    } catch (err) {
      console.error("PDF error", err);
      showSnackbar("Could not generate PDF", "error");
    } finally {
      setIsGenerating(false);
    }
  };



  // ------- React UI -------

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2, ml: 2 }}>Loading job details…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ maxWidth: 900, mx: "auto", textAlign: "center" }}>
          <Typography variant="h5" color="error">
            {error}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Box>
    );
  }

  if (!job) return null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        p: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
            >
              Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPDF}
              disabled={isGenerating}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            >
              {isGenerating ? "Generating…" : "Download PDF"}
            </Button>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                width: 56,
                height: 56,
              }}
            >
              {getStatusIcon(job.status)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                Job Sheet Viewer
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Job #{job.jobReferenceNumber || job.id} •{" "}
                {job.jobTemplateName}
              </Typography>
            </Box>
            <Chip
              label={job.status}
              color={getStatusColor(job.status)}
              icon={getStatusIcon(job.status)}
              sx={{ fontSize: "0.9rem", fontWeight: 600 }}
            />
          </Stack>
        </Box>

        {/* Statistics Dashboard */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats.answeredQuestions}/{stats.totalQuestions}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Questions Answered
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                color: "white",
              }}
            >
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats.completionPercentage}%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Completion Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                color: "white",
              }}
            >
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats.totalPhotos}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Photos Uploaded
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                color: "white",
              }}
            >
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats.hasSignature ? "✓" : "✗"}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Customer Signature
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* LEFT COLUMN */}
          <Grid item xs={12} md={4}>
            {/* Client Details */}
            <Paper
              elevation={4}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: "background.paper",
                mb: 3,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "#667eea" }}>
                  <BusinessIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Client Details
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Client"
                    secondary={job.customerName || "N/A"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Address"
                    secondary={job.siteAddress || "N/A"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Contact"
                    secondary={job.siteContact || "N/A"}
                  />
                </ListItem>
              </List>
            </Paper>

            {/* Job Details */}
            <Paper
              elevation={4}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: "background.paper",
                mb: 3,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "#f093fb" }}>
                  <AssignmentIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Job Details
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Job Type"
                    secondary={job.jobTemplateName || "N/A"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Engineer"
                    secondary={job.engineerName || "N/A"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Vehicle"
                    secondary={job.vehicleReg || "N/A"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Created"
                    secondary={formatDateShort(job.createdAt)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Completed"
                    secondary={formatDateShort(job.completionData?.completedAt)}
                  />
                </ListItem>
              </List>
            </Paper>

            {/* Signature */}
            {job.completionData?.signatureUrl && (
              <Paper
                elevation={4}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: "background.paper",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: "#10b981" }}>
                    <CheckCircleIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Customer Sign-off
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Name:</strong>{" "}
                  {job.completionData?.customerName || "N/A"}
                </Typography>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 1,
                    bgcolor: "background.default",
                  }}
                >
                  <img
                    src={job.completionData.signatureUrl}
                    alt="Customer signature"
                    style={{ width: "100%", height: "auto" }}
                  />
                </Box>
              </Paper>
            )}
          </Grid>

          {/* RIGHT COLUMN */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={4}
              sx={{
                borderRadius: 3,
                bgcolor: "background.paper",
                overflow: "hidden",
              }}
            >
              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Tab
                  icon={<DescriptionIcon />}
                  iconPosition="start"
                  label="Questions & Answers"
                />
                <Tab
                  icon={<PhotoLibraryIcon />}
                  iconPosition="start"
                  label={`Photos (${stats.totalPhotos})`}
                />
              </Tabs>

              {/* Tab 0: Questions & Answers */}
              {activeTab === 0 && (
                <Box sx={{ p: 3 }}>
                  {textFields.length === 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography color="text.secondary">
                        No questions answered yet
                      </Typography>
                    </Box>
                  )}
                  <Grid container spacing={2}>
                    {textFields.map((field) => {
                      if (field.type === "section") {
                        return (
                          <Grid item xs={12} key={field.id}>
                            <Box
                              sx={{
                                mt: 2,
                                pt: 2,
                                borderTop: "2px solid",
                                borderColor: "primary.main",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  color: "primary.main",
                                }}
                              >
                                {field.label}
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      }
                      return (
                        <Grid item xs={12} md={6} key={field.id}>
                          <Paper
                            variant="outlined"
                            sx={{ p: 2, borderRadius: 2 }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, mb: 1 }}
                            >
                              {field.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {field.type === "checkbox"
                                ? field.value
                                  ? "Yes"
                                  : "No"
                                : field.value == null || field.value === ""
                                ? "N/A"
                                : String(field.value)}
                            </Typography>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {/* Tab 1: Photos */}
              {activeTab === 1 && (
                <Box sx={{ p: 3 }}>
                  {imageFields.length === 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <PhotoLibraryIcon
                        sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                      />
                      <Typography color="text.secondary">
                        No photos uploaded yet
                      </Typography>
                    </Box>
                  )}
                  <ImageList cols={3} gap={16}>
                    {imageFields.map((field, idx) => (
                      <ImageListItem
                        key={idx}
                        sx={{
                          cursor: "pointer",
                          borderRadius: 2,
                          overflow: "hidden",
                          "&:hover": {
                            opacity: 0.8,
                          },
                        }}
                        onClick={() =>
                          setPhotoDialog({ open: true, photos: imageFields })
                        }
                      >
                        <img
                          src={field.value}
                          alt={field.label || `Photo ${idx + 1}`}
                          loading="lazy"
                          style={{ height: 200, objectFit: "cover" }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: "rgba(0,0,0,0.6)",
                            color: "white",
                            p: 1,
                          }}
                        >
                          <Typography variant="caption">
                            {field.label}
                          </Typography>
                        </Box>
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Photo Dialog */}
        <Dialog
          open={photoDialog.open}
          onClose={() => setPhotoDialog({ open: false, photos: [] })}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Job Photos</Typography>
              <IconButton
                onClick={() => setPhotoDialog({ open: false, photos: [] })}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <ImageList cols={2} gap={16}>
              {photoDialog.photos.map((field, idx) => (
                <ImageListItem key={idx}>
                  <img
                    src={field.value}
                    alt={field.label || `Photo ${idx + 1}`}
                    loading="lazy"
                    style={{ width: "100%", height: "auto" }}
                  />
                  <Typography variant="caption" sx={{ mt: 1 }}>
                    {field.label}
                  </Typography>
                </ImageListItem>
              ))}
            </ImageList>
          </DialogContent>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ open: false, message: "", severity: "success" })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ open: false, message: "", severity: "success" })}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}

export default JobViewer;